import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { propertyAPI } from '../../services/api';
import SuccessModal from '../Common/SuccessModal';
import ErrorModal from '../Common/ErrorModal';
import PrintHeader from '../Common/PrintHeader';
import './PropertyCreate.css';

const PropertyView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(1);
  const [saving, setSaving] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [propertyId] = useState(parseInt(id));
  const [dropdowns, setDropdowns] = useState({
    cities: [],
    areas: [],
    districts: [],
    nationalities: [],
    identities: [],
    banks: [],
    realEstateTypes: [],
    realEstateUsages: [],
    unitTypes: [],
    unitUsages: [],
    facilities: [],
    unitFacilities: []
  });

  // Tab 1: Property Information
  const [tab1Data, setTab1Data] = useState({
    code: '',
    nameEng: '',
    nameAr: '',
    ownerDocumentType: '1',
    titleDeed: '',
    identityNumber: '',
    crNumber: '',
    issueDate: '',
    placeTypeCityId: ''
  });

  // Tab 2: Ownership Information
  const [ownerships, setOwnerships] = useState([]);
  const [showOwnershipModal, setShowOwnershipModal] = useState(false);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [editingOwnershipIndex, setEditingOwnershipIndex] = useState(null);
  const [ownershipFormData, setOwnershipFormData] = useState({
    ownershipNameAr: '',
    ownershipNameEng: '',
    dob: '',
    nationalityId: '',
    identityTypeId: '',
    identityNumber: '',
    mobileNumberCountryCode: '',
    mobileNumber: '',
    emailId: '',
    bankId: '',
    bankAccountNumber: '',
    areaId: '',
    cityId: '',
    districtId: '',
    streetName: '',
    buildingNumber: '',
    additionalNumber: '',
    postalCode: '',
    isAgent: false,
    agentNumber: '',
    ownerId: ''
  });

  // Tab 3: Property Details
  const [tab3Data, setTab3Data] = useState({
    municipalLicenseNumber: '',
    realEstateTypeId: '',
    realEstateStatus: '',
    realEstateUsageId: '',
    realEstateNumber: '',
    unitFloor: '',
    realEstateAge: '',
    unitTotalNumber: '',
    floorsTotalNumber: '',
    areaId: '',
    cityId: '',
    districtId: '',
    streetName: '',
    buildingNumber: '',
    additionalNumber: '',
    postalCode: '',
    isPartOfCompound: false,
    selectedFacilities: {} // { facilityId: quantity }
  });

  // Tab 4: Units Details
  const [units, setUnits] = useState([]);
  const [showUnitModal, setShowUnitModal] = useState(false);
  const [editingUnitIndex, setEditingUnitIndex] = useState(null);
  const [unitFormData, setUnitFormData] = useState({
    unitNumber: '',
    unitTypeId: '',
    unitUsageId: '',
    floorNumber: '',
    unitArea: '',
    numberOfRooms: '',
    establishedDate: '',
    unitDirection: '',
    unitFinishing: '',
    unitFrontLength: '',
    unitLength: '',
    unitWidth: '',
    unitHeight: '',
    includeMezzanine: false,
    furnished: false,
    kitchenCabinets: false,
    electricityMeter: '',
    gasMeterNo: '',
    waterMeter: '',
    countOfParking: '',
    selectedFacilities: {} // { facilityId: quantity }
  });

  // Tab 5: Attachments
  const [attachments, setAttachments] = useState([]);
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [editingAttachmentIndex, setEditingAttachmentIndex] = useState(null);
  const [attachmentFormData, setAttachmentFormData] = useState({
    name: '',
    file: null,
    filePath: '' // For existing attachments
  });

  const [errors, setErrors] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    loadDropdowns();
    loadProperty();
  }, [id]);

  const loadDropdowns = async () => {
    try {
      const response = await propertyAPI.getDropdowns();
      const data = response.data || {};
      // Handle both camelCase and PascalCase API responses
      const dropdownData = {
        cities: data.Cities || data.cities || [],
        areas: data.Areas || data.areas || [],
        districts: data.Districts || data.districts || [],
        nationalities: data.Nationalities || data.nationalities || [],
        identities: data.IdentityTypes || data.identityTypes || data.Identities || data.identities || [],
        banks: data.Banks || data.banks || [],
        realEstateTypes: data.RealEstateTypes || data.realEstateTypes || [],
        realEstateUsages: data.RealEstateUsages || data.realEstateUsages || [],
        unitTypes: data.UnitTypes || data.unitTypes || [],
        unitUsages: data.UnitUsages || data.unitUsages || [],
        facilities: data.FacilitiesAndAmenities || data.facilitiesAndAmenities || [],
        unitFacilities: data.UnitFacilitiesAndAmenities || data.unitFacilitiesAndAmenities || []
      };
      setDropdowns(dropdownData);
    } catch (err) {
      console.error('Failed to load dropdowns:', err);
    }
  };

  const loadProperty = async () => {
    try {
      setLoadingData(true);
      const response = await propertyAPI.getById(id);
      const data = response.data || {};

      // Populate Tab 1: Property Information
      setTab1Data({
        code: data.code || '',
        nameEng: data.nameEng || data.NameEng || '',
        nameAr: data.nameAr || data.NameAr || '',
        ownerDocumentType: (data.ownerDocumentType || data.OwnerDocumentType || '1').toString(),
        titleDeed: data.titleDeed || data.TitleDeed || '',
        identityNumber: data.identityNumber || data.IdentityNumber || '',
        crNumber: data.crNumber || data.CrNumber || '',
        issueDate: data.issueDate || data.IssueDate ? (data.issueDate || data.IssueDate).split('T')[0] : '',
        placeTypeCityId: (data.placeTypeCityId || data.PlaceTypeCityId)?.toString() || ''
      });

      // Populate Tab 2: Ownership Information
      const loadedOwnerships = (data.propertyOwnerships || data.PropertyOwnerships || []).map(po => ({
        id: po.id || po.Id || 0,
        ownershipNameAr: po.ownershipNameAr || po.OwnershipNameAr || '',
        ownershipNameEng: po.ownershipNameEng || po.OwnershipNameEng || '',
        dob: po.dob || po.DOB ? (po.dob || po.DOB).split('T')[0] : '',
        nationalityId: (po.nationalityId || po.NationalityId)?.toString() || '',
        identityTypeId: (po.identityTypeId || po.IdentityTypeId)?.toString() || '',
        identityNumber: po.identityNumber || po.IdentityNumber || '',
        mobileNumberCountryCode: po.mobileNumberCountryCode || po.MobileNumberCountryCode || '',
        mobileNumber: po.mobileNumber || po.MobileNumber || '',
        emailId: po.emailId || po.EmailId || '',
        bankId: (po.bankId || po.BankId)?.toString() || '',
        bankAccountNumber: po.bankAccountNumber || po.BankAccountNumber || '',
        areaId: (po.areaId || po.AreaId)?.toString() || '',
        cityId: (po.cityId || po.CityId)?.toString() || '',
        districtId: (po.districtId || po.DistrictId)?.toString() || '',
        streetName: po.streetName || po.StreetName || '',
        buildingNumber: po.buildingNumber || po.BuildingNumber || '',
        additionalNumber: po.additionalNumber || po.AdditionalNumber || '',
        postalCode: po.postalCode || po.PostalCode || '',
        isAgent: po.isAgent || po.IsAgent || false,
        agentNumber: po.agentNumber || po.AgentNumber || '',
        ownerId: (po.ownerId || po.OwnerId)?.toString() || ''
      }));
      setOwnerships(loadedOwnerships);

      // Populate Tab 3: Property Details
      const facilitiesMap = {};
      (data.propertyFacilitiesAndAmenities || data.PropertyFacilitiesAndAmenities || []).forEach(pfa => {
        const facilityId = pfa.facilitiesAndAmenitiesId || pfa.FacilitiesAndAmenitiesId;
        const quantity = pfa.quantity || pfa.Quantity || 1;
        facilitiesMap[facilityId] = quantity;
      });

      setTab3Data({
        municipalLicenseNumber: data.municipalLicenseNumber || data.MunicipalLicenseNumber || '',
        realEstateTypeId: (data.realEstateTypeId || data.RealEstateTypeId)?.toString() || '',
        realEstateStatus: (data.realEstateStatus || data.RealEstateStatus)?.toString() || '',
        realEstateUsageId: (data.realEstateUsageId || data.RealEstateUsageId)?.toString() || '',
        realEstateNumber: data.realEstateNumber || data.RealEstateNumber || '',
        unitFloor: (data.unitFloor || data.UnitFloor)?.toString() || '',
        realEstateAge: (data.realEstateAge || data.RealEstateAge)?.toString() || '',
        unitTotalNumber: (data.unitTotalNumber || data.UnitTotalNumber)?.toString() || '',
        floorsTotalNumber: (data.floorsTotalNumber || data.FloorsTotalNumber)?.toString() || '',
        areaId: (data.areaId || data.AreaId)?.toString() || '',
        cityId: (data.cityId || data.CityId)?.toString() || '',
        districtId: (data.districtId || data.DistrictId)?.toString() || '',
        streetName: data.streetName || data.StreetName || '',
        buildingNumber: data.buildingNumber || data.BuildingNumber || '',
        additionalNumber: data.additionalNumber || data.AdditionalNumber || '',
        postalCode: data.postalCode || data.PostalCode || '',
        isPartOfCompound: data.isPartOfCompound || data.IsPartOfCompound || false,
        selectedFacilities: facilitiesMap
      });

      // Populate Tab 4: Units Details
      const loadedUnits = (data.propertyUnits || data.PropertyUnits || []).map(pu => {
        const unitFacilitiesMap = {};
        (pu.propertyUnitFacilitiesAndAmenities || pu.PropertyUnitFacilitiesAndAmenities || []).forEach(pufa => {
          const facilityId = pufa.unitFacilitiesAndAmenitiesId || pufa.UnitFacilitiesAndAmenitiesId;
          unitFacilitiesMap[facilityId] = 1; // Default quantity
        });

        return {
          id: pu.id || pu.Id || 0,
          unitNumber: pu.unitNumber || pu.UnitNumber || '',
          unitTypeId: (pu.unitTypeId || pu.UnitTypeId)?.toString() || '',
          unitUsageId: (pu.unitUsageId || pu.UnitUsageId)?.toString() || '',
          floorNumber: (pu.floorNumber || pu.FloorNumber)?.toString() || '',
          unitArea: (pu.unitArea || pu.UnitArea)?.toString() || '',
          numberOfRooms: (pu.numberOfRooms || pu.NumberOfRooms)?.toString() || '',
          establishedDate: pu.establishedDate || pu.EstablishedDate ? (pu.establishedDate || pu.EstablishedDate).split('T')[0] : '',
          unitDirection: (pu.unitDirection || pu.UnitDirection)?.toString() || '',
          unitFinishing: (pu.unitFinishing || pu.UnitFinishing)?.toString() || '',
          unitFrontLength: (pu.unitFrontLength || pu.UnitFrontLength)?.toString() || '',
          unitLength: (pu.unitLength || pu.UnitLength)?.toString() || '',
          unitWidth: (pu.unitWidth || pu.UnitWidth)?.toString() || '',
          unitHeight: (pu.unitHeight || pu.UnitHeight)?.toString() || '',
          includeMezzanine: pu.includeMezzanine || pu.IncludeMezzanine || false,
          furnished: pu.furnished || pu.Furnished || false,
          kitchenCabinets: pu.kitchenCabinets || pu.KitchenCabinets || false,
          electricityMeter: pu.electricityMeter || pu.ElectricityMeter || '',
          gasMeterNo: pu.gasMeterNo || pu.GasMeterNo || '',
          waterMeter: pu.waterMeter || pu.WaterMeter || '',
          countOfParking: (pu.countOfParking || pu.CountOfParking)?.toString() || '',
          selectedFacilities: unitFacilitiesMap,
          unitTypeName: (pu.unitType || pu.UnitType)?.nameEng || (pu.unitType || pu.UnitType)?.NameEng || '',
          unitUsageName: (pu.unitUsage || pu.UnitUsage)?.nameEng || (pu.unitUsage || pu.UnitUsage)?.NameEng || '',
          unitDirectionName: '',
          unitFinishingName: ''
        };
      });
      setUnits(loadedUnits);

      // Populate Tab 5: Attachments
      const loadedAttachments = (data.propertyAttachments || data.PropertyAttachments || []).map(pa => ({
        id: pa.id || pa.Id || 0,
        name: pa.name || pa.Name || '',
        file: null,
        filePath: pa.path || pa.Path || ''
      }));
      setAttachments(loadedAttachments);
    } catch (err) {
      setErrorMessage(t('property.view.messages.loadError'));
      setShowErrorModal(true);
    } finally {
      setLoadingData(false);
    }
  };

  const validateTab1 = () => {
    const newErrors = {};
    if (!tab1Data.code.trim()) newErrors.code = 'Code is required';
    if (!tab1Data.nameEng.trim()) newErrors.nameEng = 'English name is required';
    if (!tab1Data.nameAr.trim()) newErrors.nameAr = 'Arabic name is required';
    if (!tab1Data.ownerDocumentType) newErrors.ownerDocumentType = 'Owner document type is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check if a tab can be accessed (for edit mode, all tabs are accessible since property exists)
  const canAccessTab = (tabNumber) => {
    // In edit mode, property already exists so all tabs are accessible
    return true;
  };

  // Handle tab click - in edit mode, all tabs are accessible
  const handleTabClick = (tabNumber) => {
    setActiveTab(tabNumber);
  };

  const handleTab1Change = (e) => {
    const { name, value, type, checked } = e.target;
    setTab1Data(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const saveTab1 = async () => {
    if (!validateTab1()) return false;

    setSaving(true);
    try {
      const submitData = {
        ...tab1Data,
        ownerDocumentType: parseInt(tab1Data.ownerDocumentType),
        placeTypeCityId: tab1Data.placeTypeCityId ? parseInt(tab1Data.placeTypeCityId) : null,
        issueDate: tab1Data.issueDate || null
      };

      let response;
      if (propertyId) {
        response = await propertyAPI.update(propertyId, submitData);
      } else {
        response = await propertyAPI.create(submitData);
        // Property ID is already set from URL params
      }
      return true;
    } catch (err) {
      setErrorMessage('Failed to save Property Information. Please try again.');
      setShowErrorModal(true);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    if (activeTab === 1) {
      const saved = await saveTab1();
      if (!saved) return;
    } else if (activeTab === 2) {
      if (!propertyId) {
        setErrorMessage('Please complete Tab 1 first.');
        setShowErrorModal(true);
        return;
      }
      // Save ownerships via Tab2 endpoint
      try {
        setSaving(true);
        // Format ownerships data to match API model exactly
        const formattedOwnerships = ownerships.map(ownership => {
          // Ensure ownerId is properly formatted - it should be null or an integer, never an array or empty string
          let ownerIdValue = null;
          if (ownership.ownerId) {
            if (Array.isArray(ownership.ownerId)) {
              // If it's an array, take the first element and convert to int
              const firstVal = ownership.ownerId[0];
              if (firstVal && firstVal.toString().trim() !== '') {
                const parsed = parseInt(firstVal.toString());
                ownerIdValue = isNaN(parsed) ? null : parsed;
              }
            } else if (typeof ownership.ownerId === 'string') {
              const trimmed = ownership.ownerId.trim();
              if (trimmed !== '') {
                const parsed = parseInt(trimmed);
                ownerIdValue = isNaN(parsed) ? null : parsed;
              }
            } else if (typeof ownership.ownerId === 'number') {
              ownerIdValue = isNaN(ownership.ownerId) ? null : ownership.ownerId;
            }
          }
          
          // Helper function to safely parse IDs
          const parseId = (value) => {
            if (!value) return null;
            if (Array.isArray(value)) {
              const parsed = parseInt(value[0]);
              return isNaN(parsed) ? null : parsed;
            }
            if (typeof value === 'string') {
              const parsed = parseInt(value);
              return isNaN(parsed) ? null : parsed;
            }
            if (typeof value === 'number') {
              return isNaN(value) ? null : value;
            }
            return null;
          };
          
          // Build ownership object with ONLY the fields expected by the API
          // Do not include any navigation properties (Property, Owner, Nationality, etc.)
          // These are server-side only and should never be sent from the client
          const formatted = {};
          
          // Only add fields that are part of the PropertyOwnership model (not navigation properties)
          if (ownership.id) formatted.id = ownership.id;
          formatted.propertyId = propertyId;
          formatted.ownershipNameAr = ownership.ownershipNameAr || '';
          formatted.ownershipNameEng = ownership.ownershipNameEng || '';
          if (ownership.dob) formatted.dob = ownership.dob;
          if (parseId(ownership.nationalityId) !== null) formatted.nationalityId = parseId(ownership.nationalityId);
          if (parseId(ownership.identityTypeId) !== null) formatted.identityTypeId = parseId(ownership.identityTypeId);
          if (ownership.identityNumber) formatted.identityNumber = ownership.identityNumber;
          if (ownership.mobileNumberCountryCode) formatted.mobileNumberCountryCode = ownership.mobileNumberCountryCode;
          if (ownership.mobileNumber) formatted.mobileNumber = ownership.mobileNumber;
          if (ownership.emailId) formatted.emailId = ownership.emailId;
          if (parseId(ownership.bankId) !== null) formatted.bankId = parseId(ownership.bankId);
          if (ownership.bankAccountNumber) formatted.bankAccountNumber = ownership.bankAccountNumber;
          if (parseId(ownership.areaId) !== null) formatted.areaId = parseId(ownership.areaId);
          if (parseId(ownership.cityId) !== null) formatted.cityId = parseId(ownership.cityId);
          if (parseId(ownership.districtId) !== null) formatted.districtId = parseId(ownership.districtId);
          if (ownership.streetName) formatted.streetName = ownership.streetName;
          if (ownership.buildingNumber) formatted.buildingNumber = ownership.buildingNumber;
          if (ownership.additionalNumber) formatted.additionalNumber = ownership.additionalNumber;
          if (ownership.postalCode) formatted.postalCode = ownership.postalCode;
          formatted.isAgent = ownership.isAgent === true;
          if (ownership.agentNumber) formatted.agentNumber = ownership.agentNumber;
          if (ownerIdValue !== null) formatted.ownerId = ownerIdValue;
          
          return formatted;
        });
        
        await propertyAPI.saveTab2(propertyId, { ownerships: formattedOwnerships });
      } catch (err) {
        setErrorMessage('Failed to save Ownership Information.');
        setShowErrorModal(true);
        setSaving(false);
        return;
      } finally {
        setSaving(false);
      }
    } else if (activeTab === 3) {
      if (!propertyId) {
        setErrorMessage('Please complete previous tabs first.');
        setShowErrorModal(true);
        return;
      }
      try {
        setSaving(true);
        // Format facilities data for API
        const selectedFacilities = Object.keys(tab3Data.selectedFacilities);
        const facilityQuantities = {};
        selectedFacilities.forEach(facilityId => {
          facilityQuantities[parseInt(facilityId)] = tab3Data.selectedFacilities[facilityId];
        });

        const submitData = {
          municipalLicenseNumber: tab3Data.municipalLicenseNumber,
          realEstateTypeId: tab3Data.realEstateTypeId ? parseInt(tab3Data.realEstateTypeId) : null,
          realEstateStatus: tab3Data.realEstateStatus ? parseInt(tab3Data.realEstateStatus) : null,
          realEstateUsageId: tab3Data.realEstateUsageId ? parseInt(tab3Data.realEstateUsageId) : null,
          realEstateNumber: tab3Data.realEstateNumber,
          unitFloor: tab3Data.unitFloor ? parseInt(tab3Data.unitFloor) : null,
          realEstateAge: tab3Data.realEstateAge ? parseInt(tab3Data.realEstateAge) : null,
          unitTotalNumber: tab3Data.unitTotalNumber ? parseInt(tab3Data.unitTotalNumber) : null,
          floorsTotalNumber: tab3Data.floorsTotalNumber ? parseInt(tab3Data.floorsTotalNumber) : null,
          areaId: tab3Data.areaId ? parseInt(tab3Data.areaId) : null,
          cityId: tab3Data.cityId ? parseInt(tab3Data.cityId) : null,
          districtId: tab3Data.districtId ? parseInt(tab3Data.districtId) : null,
          streetName: tab3Data.streetName,
          buildingNumber: tab3Data.buildingNumber,
          additionalNumber: tab3Data.additionalNumber,
          postalCode: tab3Data.postalCode,
          isPartOfCompound: tab3Data.isPartOfCompound,
          selectedFacilitiesAndAmenities: selectedFacilities.map(id => parseInt(id)),
          facilityQuantities: facilityQuantities
        };
        await propertyAPI.saveTab3(propertyId, submitData);
      } catch (err) {
        setErrorMessage('Failed to save Property Details.');
        setShowErrorModal(true);
        setSaving(false);
        return;
      } finally {
        setSaving(false);
      }
    } else if (activeTab === 4) {
      if (!propertyId) {
        setErrorMessage('Please complete previous tabs first.');
        setShowErrorModal(true);
        return;
      }
      try {
        setSaving(true);
        await propertyAPI.saveTab4(propertyId, { units });
      } catch (err) {
        setErrorMessage('Failed to save Units Details.');
        setShowErrorModal(true);
        setSaving(false);
        return;
      } finally {
        setSaving(false);
      }
    }

    if (activeTab < 5) {
      setActiveTab(activeTab + 1);
    }
  };

  const handlePrevious = () => {
    if (activeTab > 1) {
      setActiveTab(activeTab - 1);
    }
  };

  const handleFinish = async () => {
    // Save attachments
    if (propertyId && attachments.length > 0) {
      try {
        setSaving(true);
        const formData = new FormData();
        attachments.forEach(file => {
          formData.append('files', file);
        });
        await propertyAPI.saveTab5(propertyId, formData);
      } catch (err) {
        setErrorMessage('Failed to save attachments.');
        setShowErrorModal(true);
        setSaving(false);
        return;
      } finally {
        setSaving(false);
      }
    }

    setShowSuccessModal(true);
    setTimeout(() => {
      navigate('/property');
    }, 1500);
  };

  // Ownership/Agent functions
  const handleAddOwner = () => {
    setOwnershipFormData({
      ownershipNameAr: '',
      ownershipNameEng: '',
      dob: '',
      nationalityId: '',
      identityTypeId: '',
      identityNumber: '',
      mobileNumberCountryCode: '',
      mobileNumber: '',
      emailId: '',
      bankId: '',
      bankAccountNumber: '',
      areaId: '',
      cityId: '',
      districtId: '',
      streetName: '',
      buildingNumber: '',
      additionalNumber: '',
      postalCode: '',
      isAgent: false,
      agentNumber: '',
      ownerId: ''
    });
    setEditingOwnershipIndex(null);
    setShowOwnershipModal(true);
  };

  const handleAddAgent = () => {
    setOwnershipFormData({
      ownershipNameAr: '',
      ownershipNameEng: '',
      dob: '',
      nationalityId: '',
      identityTypeId: '',
      identityNumber: '',
      mobileNumberCountryCode: '',
      mobileNumber: '',
      emailId: '',
      bankId: '',
      bankAccountNumber: '',
      areaId: '',
      cityId: '',
      districtId: '',
      streetName: '',
      buildingNumber: '',
      additionalNumber: '',
      postalCode: '',
      isAgent: true,
      agentNumber: '',
      ownerId: ''
    });
    setEditingOwnershipIndex(null);
    setShowAgentModal(true);
  };

  const handleOwnershipFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setOwnershipFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSaveOwnership = () => {
    if (!ownershipFormData.ownershipNameAr.trim() || !ownershipFormData.ownershipNameEng.trim()) {
      setErrorMessage('Owner/Agent name (Arabic and English) is required.');
      setShowErrorModal(true);
      return;
    }

    const newOwnership = {
      ...ownershipFormData,
      nationalityId: ownershipFormData.nationalityId ? parseInt(ownershipFormData.nationalityId) : null,
      identityTypeId: ownershipFormData.identityTypeId ? parseInt(ownershipFormData.identityTypeId) : null,
      bankId: ownershipFormData.bankId ? parseInt(ownershipFormData.bankId) : null,
      areaId: ownershipFormData.areaId ? parseInt(ownershipFormData.areaId) : null,
      cityId: ownershipFormData.cityId ? parseInt(ownershipFormData.cityId) : null,
      districtId: ownershipFormData.districtId ? parseInt(ownershipFormData.districtId) : null,
      ownerId: ownershipFormData.ownerId ? parseInt(ownershipFormData.ownerId) : null,
      dob: ownershipFormData.dob || null
    };

    if (editingOwnershipIndex !== null) {
      const updated = [...ownerships];
      updated[editingOwnershipIndex] = newOwnership;
      setOwnerships(updated);
    } else {
      setOwnerships([...ownerships, newOwnership]);
    }

    setShowOwnershipModal(false);
    setShowAgentModal(false);
    setEditingOwnershipIndex(null);
  };

  const handleEditOwnership = (index) => {
    setOwnershipFormData({
      ...ownerships[index],
      nationalityId: ownerships[index].nationalityId?.toString() || '',
      identityTypeId: ownerships[index].identityTypeId?.toString() || '',
      bankId: ownerships[index].bankId?.toString() || '',
      areaId: ownerships[index].areaId?.toString() || '',
      cityId: ownerships[index].cityId?.toString() || '',
      districtId: ownerships[index].districtId?.toString() || '',
      ownerId: ownerships[index].ownerId?.toString() || '',
      dob: ownerships[index].dob || ''
    });
    setEditingOwnershipIndex(index);
    if (ownerships[index].isAgent) {
      setShowAgentModal(true);
    } else {
      setShowOwnershipModal(true);
    }
  };

  const handleDeleteOwnership = (index) => {
    setOwnerships(ownerships.filter((_, i) => i !== index));
  };

  // Unit handlers
  const handleAddUnit = () => {
    // Ensure dropdowns are loaded
    if (dropdowns.unitFacilities.length === 0 && dropdowns.unitTypes.length === 0) {
      loadDropdowns();
    }
    setEditingUnitIndex(null);
    setUnitFormData({
      unitNumber: '',
      unitTypeId: '',
      unitUsageId: '',
      floorNumber: '',
      unitArea: '',
      numberOfRooms: '',
      establishedDate: '',
      unitDirection: '',
      unitFinishing: '',
      unitFrontLength: '',
      unitLength: '',
      unitWidth: '',
      unitHeight: '',
      includeMezzanine: false,
      furnished: false,
      kitchenCabinets: false,
      electricityMeter: '',
      gasMeterNo: '',
      waterMeter: '',
      countOfParking: '',
      selectedFacilities: {}
    });
    setShowUnitModal(true);
  };

  const handleEditUnit = (index) => {
    const unit = units[index];
    setEditingUnitIndex(index);
    setUnitFormData({
      unitNumber: unit.unitNumber || '',
      unitTypeId: unit.unitTypeId || '',
      unitUsageId: unit.unitUsageId || '',
      floorNumber: unit.floorNumber || '',
      unitArea: unit.unitArea || '',
      numberOfRooms: unit.numberOfRooms || '',
      establishedDate: unit.establishedDate || '',
      unitDirection: unit.unitDirection || '',
      unitFinishing: unit.unitFinishing || '',
      unitFrontLength: unit.unitFrontLength || '',
      unitLength: unit.unitLength || '',
      unitWidth: unit.unitWidth || '',
      unitHeight: unit.unitHeight || '',
      includeMezzanine: unit.includeMezzanine || false,
      furnished: unit.furnished || false,
      kitchenCabinets: unit.kitchenCabinets || false,
      electricityMeter: unit.electricityMeter || '',
      gasMeterNo: unit.gasMeterNo || '',
      waterMeter: unit.waterMeter || '',
      countOfParking: unit.countOfParking || '',
      selectedFacilities: unit.selectedFacilities || {}
    });
    setShowUnitModal(true);
  };

  const handleSaveUnit = () => {
    // Find unit type and usage names
    const unitType = (dropdowns.unitTypes || []).find(t => t.id === parseInt(unitFormData.unitTypeId));
    const unitUsage = (dropdowns.unitUsages || []).find(u => u.id === parseInt(unitFormData.unitUsageId));
    
    // Map direction enum values
    const directionMap = {
      '1': 'North', '2': 'South', '3': 'East', '4': 'West',
      '5': 'NorthEast', '6': 'NorthWest', '7': 'SouthEast', '8': 'SouthWest'
    };
    const directionName = unitFormData.unitDirection ? directionMap[unitFormData.unitDirection] : '';

    // Map finishing enum values
    const finishingMap = {
      '1': 'Basic', '2': 'Standard', '3': 'Premium', '4': 'Luxury', '5': 'Unfinished'
    };
    const finishingName = unitFormData.unitFinishing ? finishingMap[unitFormData.unitFinishing] : '';

    const unitData = {
      ...unitFormData,
      unitTypeName: unitType ? (unitType.nameEng || unitType.NameEng) : '',
      unitUsageName: unitUsage ? (unitUsage.nameEng || unitUsage.NameEng) : '',
      unitDirectionName: directionName,
      unitFinishingName: finishingName
    };

    if (editingUnitIndex !== null) {
      // Update existing unit
      const updatedUnits = [...units];
      updatedUnits[editingUnitIndex] = unitData;
      setUnits(updatedUnits);
    } else {
      // Add new unit
      setUnits([...units, unitData]);
    }
    setShowUnitModal(false);
  };

  const handleDeleteUnit = (index) => {
    setUnits(units.filter((_, i) => i !== index));
  };

  const handleUnitFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setUnitFormData({
      ...unitFormData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Attachment handlers
  const handleAddAttachment = () => {
    setEditingAttachmentIndex(null);
    setAttachmentFormData({
      name: '',
      file: null,
      filePath: ''
    });
    setShowAttachmentModal(true);
  };

  const handleEditAttachment = (index) => {
    const attachment = attachments[index];
    setEditingAttachmentIndex(index);
    setAttachmentFormData({
      name: attachment.name,
      file: attachment.file || null,
      filePath: attachment.filePath || ''
    });
    setShowAttachmentModal(true);
  };

  const handleSaveAttachment = () => {
    if (!attachmentFormData.name.trim()) {
      setErrorMessage('Please enter an attachment name.');
      setShowErrorModal(true);
      return;
    }

    if (!attachmentFormData.file && !attachmentFormData.filePath && editingAttachmentIndex === null) {
      setErrorMessage('Please select a file to upload.');
      setShowErrorModal(true);
      return;
    }

    const attachmentData = {
      name: attachmentFormData.name,
      file: attachmentFormData.file,
      filePath: attachmentFormData.filePath
    };

    if (editingAttachmentIndex !== null) {
      // Update existing attachment
      const updatedAttachments = [...attachments];
      updatedAttachments[editingAttachmentIndex] = attachmentData;
      setAttachments(updatedAttachments);
    } else {
      // Add new attachment
      setAttachments([...attachments, attachmentData]);
    }
    setShowAttachmentModal(false);
  };

  const handleDeleteAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      if (attachment.file) {
        // For new files not yet uploaded, create blob URL and download
        const url = URL.createObjectURL(attachment.file);
        const link = document.createElement('a');
        link.href = url;
        link.download = attachment.file.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else if (attachment.filePath) {
        // For existing files, download from server
        const baseUrl = process.env.REACT_APP_API_URL || 'https://nadeedapi.urapp4u.com';
        const fileUrl = `${baseUrl.replace('/api', '')}${attachment.filePath}`;
        
        // Fetch the file as blob to force download
        const response = await fetch(fileUrl);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = attachment.name || 'attachment';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      setErrorMessage('Failed to download attachment.');
      setShowErrorModal(true);
    }
  };

  const handleViewAttachment = (attachment) => {
    try {
      if (attachment.file) {
        // For new files not yet uploaded, create blob URL and open
        const url = URL.createObjectURL(attachment.file);
        window.open(url, '_blank');
        // Clean up after a delay
        setTimeout(() => URL.revokeObjectURL(url), 100);
      } else if (attachment.filePath) {
        // For existing files, open from server
        const baseUrl = process.env.REACT_APP_API_URL || 'https://nadeedapi.urapp4u.com';
        const fileUrl = `${baseUrl.replace('/api', '')}${attachment.filePath}`;
        window.open(fileUrl, '_blank');
      }
    } catch (err) {
      setErrorMessage('Failed to view attachment.');
      setShowErrorModal(true);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleAttachmentFormChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'file') {
      setAttachmentFormData({
        ...attachmentFormData,
        file: files && files.length > 0 ? files[0] : null
      });
    } else {
      setAttachmentFormData({
        ...attachmentFormData,
        [name]: value
      });
    }
  };
  if (loadingData) {
    return (
      <div className="page-container">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Print Header - Only visible when printing */}
      <PrintHeader title={t('property.details', 'Property Details')} />

      <div className="property-form-container">
        {/* Tabs Navigation */}
        <div className="property-tabs">
          <div 
            className={`tab-header ${activeTab === 1 ? 'active' : ''} ${!canAccessTab(1) ? 'disabled' : ''}`} 
            onClick={() => handleTabClick(1)}
          >
            <i className="fas fa-info-circle"></i>
            <span>{t('property.view.tabs.propertyInformation')}</span>
          </div>
          <div 
            className={`tab-header ${activeTab === 2 ? 'active' : ''} ${!canAccessTab(2) ? 'disabled' : ''}`} 
            onClick={() => handleTabClick(2)}
          >
            <i className="fas fa-users"></i>
            <span>{t('property.view.tabs.ownershipInformation')}</span>
          </div>
          <div 
            className={`tab-header ${activeTab === 3 ? 'active' : ''} ${!canAccessTab(3) ? 'disabled' : ''}`} 
            onClick={() => handleTabClick(3)}
          >
            <i className="fas fa-building"></i>
            <span>{t('property.view.tabs.propertyDetails')}</span>
          </div>
          <div 
            className={`tab-header ${activeTab === 4 ? 'active' : ''} ${!canAccessTab(4) ? 'disabled' : ''}`} 
            onClick={() => handleTabClick(4)}
          >
            <i className="fas fa-door-open"></i>
            <span>{t('property.view.tabs.unitsDetails')}</span>
          </div>
          <div 
            className={`tab-header ${activeTab === 5 ? 'active' : ''} ${!canAccessTab(5) ? 'disabled' : ''}`} 
            onClick={() => handleTabClick(5)}
          >
            <i className="fas fa-paperclip"></i>
            <span>{t('property.view.tabs.attachments')}</span>
          </div>
        </div>

        {/* Tab Content */}
        <div className="property-tab-content">
          {/* Print View - Show all tabs */}
          <div className="print-all-tabs">
            {/* Tab 1: Property Information */}
            <div className="tab-panel print-tab-section">
              <h3 className="tab-title print-section-title">
                <i className="fas fa-info-circle"></i> {t('property.view.tabs.propertyInformation')}
              </h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-code"></i> {t('property.create.fields.tab1.code')} *
                  </label>
                  <input
                    type="text"
                    className={`form-input ${errors.code ? 'is-invalid' : ''}`}
                    name="code"
                    value={tab1Data.code}
                    readOnly
                    disabled
                    onChange={() => {}}
                    placeholder={t('property.create.fields.tab1.placeholders.code')}
                  />
                  {errors.code && <div className="error-message">{errors.code}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-font"></i> {t('property.create.fields.tab1.nameEng')} *
                  </label>
                  <input
                    type="text"
                    className={`form-input ${errors.nameEng ? 'is-invalid' : ''}`}
                    name="nameEng"
                    value={tab1Data.nameEng}
                    readOnly
                    disabled
                    onChange={() => {}}
                    placeholder={t('property.create.fields.tab1.placeholders.nameEng')}
                  />
                  {errors.nameEng && <div className="error-message">{errors.nameEng}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-language"></i> {t('property.create.fields.tab1.nameAr')} *
                  </label>
                  <input
                    type="text"
                    className={`form-input ${errors.nameAr ? 'is-invalid' : ''}`}
                    name="nameAr"
                    value={tab1Data.nameAr}
                    readOnly
                    disabled
                    onChange={() => {}}
                    placeholder={t('property.create.fields.tab1.placeholders.nameAr')}
                  />
                  {errors.nameAr && <div className="error-message">{errors.nameAr}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-file-alt"></i> {t('property.create.fields.tab1.ownerDocumentType')} *
                  </label>
                  <select
                    className={`form-input ${errors.ownerDocumentType ? 'is-invalid' : ''}`}
                    name="ownerDocumentType"
                    value={tab1Data.ownerDocumentType}
                    readOnly
                    disabled
                    onChange={() => {}}
                  >
                    <option value="">-- {t('property.create.fields.tab1.select')} --</option>
                    <option value="1">{t('property.create.fields.tab1.options.electronicTitleDeed')}</option>
                    <option value="2">{t('property.create.fields.tab1.options.propertyRegistryTitleDeed')}</option>
                  </select>
                  {errors.ownerDocumentType && <div className="error-message">{errors.ownerDocumentType}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-hashtag"></i> {t('property.create.fields.tab1.titleDeed')}
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    name="titleDeed"
                    value={tab1Data.titleDeed}
                    readOnly
                    disabled
                    onChange={() => {}}
                    placeholder={t('property.create.fields.tab1.placeholders.titleDeed')}
                  />
                </div>

                {tab1Data.ownerDocumentType === '1' && (
                  <div className="form-group">
                    <label className="form-label">
                      <i className="fas fa-id-card"></i> {t('property.create.fields.tab1.identityNumber')}
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      name="identityNumber"
                      value={tab1Data.identityNumber}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder={t('property.create.fields.tab1.placeholders.identityNumber')}
                    />
                  </div>
                )}

                {tab1Data.ownerDocumentType === '2' && (
                  <div className="form-group">
                    <label className="form-label">
                      <i className="fas fa-building"></i> {t('property.create.fields.tab1.crNumber')}
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      name="crNumber"
                      value={tab1Data.crNumber}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder={t('property.create.fields.tab1.placeholders.crNumber')}
                    />
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-calendar"></i> {t('property.create.fields.tab1.issueDate')}
                  </label>
                  <input
                    type="date"
                    className="form-input"
                    name="issueDate"
                    value={tab1Data.issueDate}
                    readOnly
                    disabled
                    onChange={() => {}}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-map-marker-alt"></i> {t('property.create.fields.tab1.placeTypeCity')}
                  </label>
                  <select
                    className="form-input"
                    name="placeTypeCityId"
                    value={tab1Data.placeTypeCityId}
                    readOnly
                    disabled
                    onChange={() => {}}
                  >
                    <option value="">-- {t('property.create.fields.tab1.selectCity')} --</option>
                    {(dropdowns.cities || []).map(city => (
                      <option key={city.id || city.Id} value={city.id || city.Id}>
                        {city.cityNameEng || city.CityNameEng}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Tab 2: Ownership Information */}
            <div className="tab-panel print-tab-section">
              <h3 className="tab-title print-section-title">
                <i className="fas fa-users"></i> {t('property.view.tabs.ownershipInformation')}
              </h3>
              <div className="ownership-table-container">
                {ownerships.length === 0 ? (
                  <div className="empty-state">{t('property.view.messages.noOwnership')}</div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>{t('property.view.tableHeaders.type')}</th>
                        <th>{t('property.view.tableHeaders.nameAr')}</th>
                        <th>{t('property.view.tableHeaders.nameEng')}</th>
                        <th>{t('property.view.tableHeaders.dob')}</th>
                        <th>{t('property.view.tableHeaders.nationality')}</th>
                        <th>{t('property.view.tableHeaders.identity')}</th>
                        <th>{t('property.view.tableHeaders.mobile')}</th>
                        <th>{t('property.view.tableHeaders.email')}</th>
                        <th>{t('property.view.tableHeaders.address')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ownerships.map((owner, index) => {
                        const nationality = (dropdowns.nationalities || []).find(n => (n.id || n.Id) == owner.nationalityId);
                        const identity = (dropdowns.identities || []).find(i => (i.id || i.Id) == owner.identityTypeId);
                        const city = (dropdowns.cities || []).find(c => (c.id || c.Id) == owner.cityId);
                        const area = (dropdowns.areas || []).find(a => (a.id || a.Id) == owner.areaId);
                        const district = (dropdowns.districts || []).find(d => (d.id || d.Id) == owner.districtId);
                        
                        return (
                          <tr key={index}>
                            <td>{owner.isAgent ? t('property.view.types.agent') : t('property.view.types.owner')}</td>
                            <td>{owner.ownershipNameAr}</td>
                            <td>{owner.ownershipNameEng}</td>
                            <td>{owner.dob || t('common.dash')}</td>
                            <td>{nationality ? (nationality.nationalityNameEng || nationality.NationalityNameEng) : t('common.dash')}</td>
                            <td>{identity ? (identity.identityNameEng || identity.IdentityNameEng) : t('common.dash')} {owner.identityNumber || ''}</td>
                            <td>{owner.mobileNumberCountryCode || ''} {owner.mobileNumber || t('common.dash')}</td>
                            <td>{owner.emailId || t('common.dash')}</td>
                            <td>
                              {[city?.cityNameEng || city?.CityNameEng, area?.areaNameEng || area?.AreaNameEng, district?.districtNameEng || district?.DistrictNameEng, owner.streetName, owner.buildingNumber].filter(Boolean).join(', ') || t('common.dash')}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Tab 3: Property Details */}
            <div className="tab-panel print-tab-section">
              <h3 className="tab-title print-section-title">
                <i className="fas fa-building"></i> {t('property.view.tabs.propertyDetails')}
              </h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-file-alt"></i> Owner Document Type
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-file-alt input-icon"></i>
                    <select
                      className="form-input"
                      name="ownerDocumentType"
                      value={tab1Data.ownerDocumentType}
                      readOnly
                      disabled
                      onChange={() => {}}
                    >
                      <option value="1">Electronic Title Deed</option>
                      <option value="2">Property Registry Title Deed</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-hashtag"></i> Title Deed
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-hashtag input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="titleDeed"
                      value={tab1Data.titleDeed}
                      readOnly
                      disabled
                      onChange={() => {}}
                    />
                  </div>
                </div>

                {tab1Data.ownerDocumentType === '1' && (
                  <div className="form-group">
                    <label className="form-label">
                      <i className="fas fa-id-card"></i> Identity Number
                    </label>
                    <div className="input-wrapper">
                      <i className="fas fa-id-card input-icon"></i>
                      <input
                        type="text"
                        className="form-input"
                        name="identityNumber"
                        value={tab1Data.identityNumber}
                        readOnly
                        disabled
                        onChange={() => {}}
                      />
                    </div>
                  </div>
                )}

                {tab1Data.ownerDocumentType === '2' && (
                  <div className="form-group">
                    <label className="form-label">
                      <i className="fas fa-building"></i> CR Number
                    </label>
                    <div className="input-wrapper">
                      <i className="fas fa-building input-icon"></i>
                      <input
                        type="text"
                        className="form-input"
                        name="crNumber"
                        value={tab1Data.crNumber}
                        readOnly
                        disabled
                        onChange={() => {}}
                      />
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-certificate"></i> Municipal License Number
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-certificate input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="municipalLicenseNumber"
                      value={tab3Data.municipalLicenseNumber}
                      readOnly
                      disabled
                      onChange={() => {}}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-building"></i> Property Type
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-building input-icon"></i>
                    <select
                      className="form-input"
                      name="realEstateTypeId"
                      value={tab3Data.realEstateTypeId}
                      readOnly
                      disabled
                      onChange={() => {}}
                    >
                      <option value="">-- {t('property.create.fields.tab1.select')} --</option>
                      {(dropdowns.realEstateTypes || []).map(type => (
                        <option key={type.id || type.Id} value={type.id || type.Id}>
                          {type.nameEng || type.NameEng}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-info-circle"></i> Property Status
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-info-circle input-icon"></i>
                    <select
                      className="form-input"
                      name="realEstateStatus"
                      value={tab3Data.realEstateStatus}
                      readOnly
                      disabled
                      onChange={() => {}}
                    >
                      <option value="">-- {t('property.create.fields.tab1.select')} --</option>
                      <option value="1">Available</option>
                      <option value="2">Rented</option>
                      <option value="3">Sold</option>
                      <option value="4">Under Maintenance</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-tag"></i> Property Usage
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-tag input-icon"></i>
                    <select
                      className="form-input"
                      name="realEstateUsageId"
                      value={tab3Data.realEstateUsageId}
                      readOnly
                      disabled
                      onChange={() => {}}
                    >
                      <option value="">-- {t('property.create.fields.tab1.select')} --</option>
                      {(dropdowns.realEstateUsages || []).map(usage => (
                        <option key={usage.id || usage.Id} value={usage.id || usage.Id}>
                          {usage.nameEng || usage.NameEng}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-hashtag"></i> Property Number
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-hashtag input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="realEstateNumber"
                      value={tab3Data.realEstateNumber}
                      readOnly
                      disabled
                      onChange={() => {}}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-layer-group"></i> Unit Floor
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-layer-group input-icon"></i>
                    <input
                      type="number"
                      className="form-input"
                      name="unitFloor"
                      value={tab3Data.unitFloor}
                      readOnly
                      disabled
                      onChange={() => {}}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-calendar-alt"></i> Property Age
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-calendar-alt input-icon"></i>
                    <input
                      type="number"
                      className="form-input"
                      name="realEstateAge"
                      value={tab3Data.realEstateAge}
                      readOnly
                      disabled
                      onChange={() => {}}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-home"></i> Unit Total Number
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-home input-icon"></i>
                    <input
                      type="number"
                      className="form-input"
                      name="unitTotalNumber"
                      value={tab3Data.unitTotalNumber}
                      readOnly
                      disabled
                      onChange={() => {}}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-building"></i> Floors Total Number
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-building input-icon"></i>
                    <input
                      type="number"
                      className="form-input"
                      name="floorsTotalNumber"
                      value={tab3Data.floorsTotalNumber}
                      readOnly
                      disabled
                      onChange={() => {}}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-map-marker-alt"></i> Area
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-map-marker-alt input-icon"></i>
                    <select
                      className="form-input"
                      name="areaId"
                      value={tab3Data.areaId}
                      readOnly
                      disabled
                      onChange={() => {}}
                    >
                      <option value="">-- {t('property.create.fields.tab1.select')} --</option>
                      {(dropdowns.areas || []).map(area => (
                        <option key={area.id || area.Id} value={area.id || area.Id}>
                          {area.areaNameEng || area.AreaNameEng}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-city"></i> City
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-city input-icon"></i>
                    <select
                      className="form-input"
                      name="cityId"
                      value={tab3Data.cityId}
                      readOnly
                      disabled
                      onChange={() => {}}
                    >
                      <option value="">-- {t('property.create.fields.tab1.select')} --</option>
                      {(dropdowns.cities || []).map(city => (
                        <option key={city.id || city.Id} value={city.id || city.Id}>
                          {city.cityNameEng || city.CityNameEng}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-map"></i> District
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-map input-icon"></i>
                    <select
                      className="form-input"
                      name="districtId"
                      value={tab3Data.districtId}
                      readOnly
                      disabled
                      onChange={() => {}}
                    >
                      <option value="">-- {t('property.create.fields.tab1.select')} --</option>
                      {(dropdowns.districts || []).map(district => (
                        <option key={district.id || district.Id} value={district.id || district.Id}>
                          {district.districtNameEng || district.DistrictNameEng}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-road"></i> Street Name
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-road input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="streetName"
                      value={tab3Data.streetName}
                      readOnly
                      disabled
                      onChange={() => {}}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-building"></i> Building Number
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-building input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="buildingNumber"
                      value={tab3Data.buildingNumber}
                      readOnly
                      disabled
                      onChange={() => {}}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-plus-circle"></i> Additional Number
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-plus-circle input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="additionalNumber"
                      value={tab3Data.additionalNumber}
                      readOnly
                      disabled
                      onChange={() => {}}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-mail-bulk"></i> Postal Code
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-mail-bulk input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="postalCode"
                      value={tab3Data.postalCode}
                      readOnly
                      disabled
                      onChange={() => {}}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-check-square"></i> Is Part Of Compound
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-check-square input-icon"></i>
                    <select
                      className="form-input"
                      name="isPartOfCompound"
                      value={tab3Data.isPartOfCompound ? 'true' : 'false'}
                      readOnly
                      disabled
                      onChange={() => {}}
                    >
                      <option value="false">No</option>
                      <option value="true">Yes</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Facilities Section */}
              {Object.keys(tab3Data.selectedFacilities || {}).length > 0 && (
                <div className="facilities-section" style={{ marginTop: '2rem' }}>
                  <label className="form-label facilities-section-label">
                    <i className="fas fa-star"></i> {t('property.create.fields.tab3.containsFacilities')}
                  </label>
                  <div className="facilities-section-container">
                    <div className="facilities-grid">
                      {(dropdowns.facilities || []).filter(facility => {
                        const facilityId = parseInt(facility.id || facility.Id);
                        return !isNaN(facilityId) && tab3Data.selectedFacilities[facilityId] !== undefined;
                      }).map(facility => {
                        const facilityId = parseInt(facility.id || facility.Id);
                        const quantity = !isNaN(facilityId) ? (tab3Data.selectedFacilities[facilityId] || 1) : 1;
                        return (
                          <div key={facilityId} className="facility-card active">
                            <div className="facility-card-content">
                              <div className="facility-icon">
                                {facility.icon || facility.Icon ? (
                                  <img src={facility.icon || facility.Icon} alt={facility.nameEng || facility.NameEng} />
                                ) : (
                                  <i className="fas fa-check-circle"></i>
                                )}
                              </div>
                              <div className="facility-info">
                                <div className="facility-name">{facility.nameEng || facility.NameEng}</div>
                                {(facility.nameAr || facility.NameAr) && (
                                  <div className="facility-name-ar">{facility.nameAr || facility.NameAr}</div>
                                )}
                              </div>
                              <div className="facility-quantity">Qty: {quantity}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Tab 4: Units Details */}
            <div className="tab-panel print-tab-section">
              <h3 className="tab-title print-section-title">
                <i className="fas fa-door-open"></i> {t('property.view.tabs.unitsDetails')}
              </h3>
              <div className="ownership-table-container">
                {units.length === 0 ? (
                  <div className="empty-state">{t('property.view.messages.noUnits')}</div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>{t('property.view.tableHeaders.unitNumber')}</th>
                        <th>{t('property.view.tableHeaders.unitType')}</th>
                        <th>{t('property.view.tableHeaders.unitUsage')}</th>
                        <th>{t('property.view.tableHeaders.floor')}</th>
                        <th>{t('property.view.tableHeaders.area')}</th>
                        <th>{t('property.view.tableHeaders.rooms')}</th>
                        <th>{t('property.view.tableHeaders.estDate')}</th>
                        <th>{t('property.view.tableHeaders.direction')}</th>
                        <th>{t('property.view.tableHeaders.finishing')}</th>
                        <th>{t('property.view.tableHeaders.furnished')}</th>
                        <th>{t('property.view.tableHeaders.parking')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {units.map((unit, index) => {
                        const unitType = (dropdowns.unitTypes || []).find(t => (t.id || t.Id) == unit.unitTypeId);
                        const unitUsage = (dropdowns.unitUsages || []).find(u => (u.id || u.Id) == unit.unitUsageId);
                        const directionMap = { '1': t('property.view.directions.north'), '2': t('property.view.directions.south'), '3': t('property.view.directions.east'), '4': t('property.view.directions.west'), '5': t('property.view.directions.northEast'), '6': t('property.view.directions.northWest'), '7': t('property.view.directions.southEast'), '8': t('property.view.directions.southWest') };
                        const finishingMap = { '1': t('property.view.finishing.basic'), '2': t('property.view.finishing.standard'), '3': t('property.view.finishing.premium'), '4': t('property.view.finishing.luxury'), '5': t('property.view.finishing.unfinished') };
                        
                        return (
                          <tr key={index}>
                            <td>{unit.unitNumber || t('common.dash')}</td>
                            <td>{unitType ? (unitType.nameEng || unitType.NameEng) : t('common.dash')}</td>
                            <td>{unitUsage ? (unitUsage.nameEng || unitUsage.NameEng) : t('common.dash')}</td>
                            <td>{unit.floorNumber || t('common.dash')}</td>
                            <td>{unit.unitArea || t('common.dash')}</td>
                            <td>{unit.numberOfRooms || t('common.dash')}</td>
                            <td>{unit.establishedDate || t('common.dash')}</td>
                            <td>{directionMap[unit.unitDirection] || t('common.dash')}</td>
                            <td>{finishingMap[unit.unitFinishing] || t('common.dash')}</td>
                            <td>{unit.furnished ? t('common.yes') : t('common.no')}</td>
                            <td>{unit.countOfParking || t('common.dash')}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Tab 5: Attachments */}
            <div className="tab-panel print-tab-section">
              <h3 className="tab-title print-section-title">
                <i className="fas fa-paperclip"></i> {t('property.view.tabs.attachments')}
              </h3>
              <div className="attachments-table-container">
                {attachments.length === 0 ? (
                  <div className="empty-state">{t('property.view.messages.noAttachments')}</div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>{t('property.view.tableHeaders.attachmentName')}</th>
                        <th>{t('property.view.tableHeaders.file')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attachments.map((attachment, index) => (
                        <tr key={index}>
                          <td>{attachment.name}</td>
                          <td>
                            {attachment.file ? (
                              <span className="text-info">
                                <i className="fas fa-file"></i> {attachment.file.name}
                                {attachment.file.size && ` (${(attachment.file.size / 1024).toFixed(2)} KB)`}
                              </span>
                            ) : attachment.filePath ? (
                              <span className="text-success">
                                <i className="fas fa-check-circle"></i> {t('property.view.messages.fileUploaded')}
                              </span>
                            ) : (
                              <span className="text-warning">{t('property.view.messages.noFileSelected')}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>

          {/* Regular View - Show only active tab (hidden in print) */}
          {/* Tab 1: Property Information */}
          {activeTab === 1 && (
            <div className="tab-panel">
              <h3 className="tab-title">{t('property.view.tabs.propertyInformation')}</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-code"></i> Code *
                  </label>
                  <input
                    type="text"
                    className={`form-input ${errors.code ? 'is-invalid' : ''}`}
                    name="code"
                    value={tab1Data.code}
                    readOnly
                    disabled
                    onChange={() => {}}
                    placeholder="Enter property code"
                  />
                  {errors.code && <div className="error-message">{errors.code}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-font"></i> Name (English) *
                  </label>
                  <input
                    type="text"
                    className={`form-input ${errors.nameEng ? 'is-invalid' : ''}`}
                    name="nameEng"
                    value={tab1Data.nameEng}
                    readOnly
                    disabled
                    onChange={() => {}}
                    placeholder="Enter English name"
                  />
                  {errors.nameEng && <div className="error-message">{errors.nameEng}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-language"></i> Name (Arabic) *
                  </label>
                  <input
                    type="text"
                    className={`form-input ${errors.nameAr ? 'is-invalid' : ''}`}
                    name="nameAr"
                    value={tab1Data.nameAr}
                    readOnly
                    disabled
                    onChange={() => {}}
                    placeholder="أدخل الاسم بالعربية"
                  />
                  {errors.nameAr && <div className="error-message">{errors.nameAr}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-file-alt"></i> Owner Document Type *
                  </label>
                  <select
                    className={`form-input ${errors.ownerDocumentType ? 'is-invalid' : ''}`}
                    name="ownerDocumentType"
                    value={tab1Data.ownerDocumentType}
                    readOnly
                    disabled
                    onChange={() => {}}
                  >
                    <option value="">-- Select --</option>
                    <option value="1">Electronic Title Deed</option>
                    <option value="2">Property Registry Title Deed</option>
                  </select>
                  {errors.ownerDocumentType && <div className="error-message">{errors.ownerDocumentType}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-hashtag"></i> Title Deed
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    name="titleDeed"
                    value={tab1Data.titleDeed}
                    readOnly
                    disabled
                    onChange={() => {}}
                    placeholder="Enter title deed number"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-calendar"></i> Issue Date
                  </label>
                  <input
                    type="date"
                    className="form-input"
                    name="issueDate"
                    value={tab1Data.issueDate}
                    readOnly
                    disabled
                    onChange={() => {}}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-map-marker-alt"></i> Place Type (City)
                  </label>
                  <select
                    className="form-input"
                    name="placeTypeCityId"
                    value={tab1Data.placeTypeCityId}
                    readOnly
                    disabled
                    onChange={() => {}}
                  >
                    <option value="">-- {t('property.create.fields.tab1.selectCity')} --</option>
                    {(dropdowns.cities || []).map(city => (
                      <option key={city.id} value={city.id}>{city.cityNameEng || city.CityNameEng}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Tab 2: Ownership Information */}
          {activeTab === 2 && (
            <div className="tab-panel">
              <h3 className="tab-title">{t('property.view.tabs.ownershipInformation')}</h3>
              <div className="ownership-table-container">
                {ownerships.length === 0 ? (
                  <div className="empty-state">{t('property.view.messages.noOwnership')}</div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>{t('property.view.tableHeaders.type')}</th>
                        <th>{t('property.view.tableHeaders.nameAr')}</th>
                        <th>{t('property.view.tableHeaders.nameEng')}</th>
                        <th>{t('property.view.tableHeaders.dob')}</th>
                        <th>{t('property.view.tableHeaders.nationality')}</th>
                        <th>{t('property.view.tableHeaders.identity')}</th>
                        <th>{t('property.view.tableHeaders.mobile')}</th>
                        <th>{t('property.view.tableHeaders.email')}</th>
                        <th>{t('property.view.tableHeaders.address')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ownerships.map((owner, index) => {
                        const nationality = (dropdowns.nationalities || []).find(n => (n.id || n.Id) == owner.nationalityId);
                        const identity = (dropdowns.identities || []).find(i => (i.id || i.Id) == owner.identityTypeId);
                        const city = (dropdowns.cities || []).find(c => (c.id || c.Id) == owner.cityId);
                        const area = (dropdowns.areas || []).find(a => (a.id || a.Id) == owner.areaId);
                        const district = (dropdowns.districts || []).find(d => (d.id || d.Id) == owner.districtId);
                        
                        return (
                          <tr key={index}>
                            <td>{owner.isAgent ? t('property.view.types.agent') : t('property.view.types.owner')}</td>
                            <td>{owner.ownershipNameAr}</td>
                            <td>{owner.ownershipNameEng}</td>
                            <td>{owner.dob || t('common.dash')}</td>
                            <td>{nationality ? (nationality.nationalityNameEng || nationality.NationalityNameEng) : t('common.dash')}</td>
                            <td>{identity ? (identity.identityNameEng || identity.IdentityNameEng) : t('common.dash')} {owner.identityNumber || ''}</td>
                            <td>{owner.mobileNumberCountryCode || ''} {owner.mobileNumber || t('common.dash')}</td>
                            <td>{owner.emailId || t('common.dash')}</td>
                            <td>
                              {[city?.cityNameEng || city?.CityNameEng, area?.areaNameEng || area?.AreaNameEng, district?.districtNameEng || district?.DistrictNameEng, owner.streetName, owner.buildingNumber].filter(Boolean).join(', ') || t('common.dash')}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {/* Tab 3: Property Details */}
          {activeTab === 3 && (
            <div className="tab-panel">
              <h3 className="tab-title">{t('property.view.tabs.propertyDetails')}</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-file-alt"></i> {t('property.create.fields.tab3.ownerDocumentType')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-file-alt input-icon"></i>
                    <select
                      className="form-input"
                      name="ownerDocumentType"
                      value={tab1Data.ownerDocumentType}
                      readOnly
                      disabled
                      onChange={() => {}}
                    >
                      <option value="1">{t('property.create.fields.tab1.options.titleDeed')}</option>
                      <option value="2">{t('property.create.fields.tab1.options.propertyRegistryTitleDeed')}</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-hashtag"></i> {t('property.create.fields.tab3.titleDeedNumbers')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-hashtag input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="titleDeed"
                      value={tab1Data.titleDeed}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder={t('property.create.fields.tab3.placeholders.titleDeed')}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-certificate"></i> {t('property.create.fields.tab3.municipalLicenseNumber')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-certificate input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="municipalLicenseNumber"
                      value={tab3Data.municipalLicenseNumber}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder={t('property.create.fields.tab3.placeholders.municipalLicense')}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-building"></i> {t('property.create.fields.tab3.propertyType')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-building input-icon"></i>
                    <select
                      className="form-input"
                      name="realEstateTypeId"
                      value={tab3Data.realEstateTypeId}
                      readOnly
                      disabled
                      onChange={() => {}}
                    >
                      <option value="">-- {t('property.create.fields.tab1.select')} --</option>
                      {(dropdowns.realEstateTypes || []).map(type => (
                        <option key={type.id} value={type.id}>{type.nameEng || type.NameEng}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-info-circle"></i> {t('property.create.fields.tab3.propertyStatus')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-info-circle input-icon"></i>
                    <select
                      className="form-input"
                      name="realEstateStatus"
                      value={tab3Data.realEstateStatus}
                      readOnly
                      disabled
                      onChange={() => {}}
                    >
                      <option value="">-- {t('property.create.fields.tab1.select')} --</option>
                      <option value="1">{t('property.create.fields.tab3.options.rent')}</option>
                      <option value="2">{t('property.create.fields.tab3.options.booked')}</option>
                      <option value="3">{t('property.create.fields.tab3.options.available')}</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-tag"></i> {t('property.create.fields.tab3.propertyUsage')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-tag input-icon"></i>
                    <select
                      className="form-input"
                      name="realEstateUsageId"
                      value={tab3Data.realEstateUsageId}
                      readOnly
                      disabled
                      onChange={() => {}}
                    >
                      <option value="">-- {t('property.create.fields.tab1.select')} --</option>
                      {(dropdowns.realEstateUsages || []).map(usage => (
                        <option key={usage.id} value={usage.id}>{usage.nameEng || usage.NameEng}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-hashtag"></i> {t('property.create.fields.tab3.propertyNumber')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-hashtag input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="realEstateNumber"
                      value={tab3Data.realEstateNumber}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder={t('property.create.fields.tab3.placeholders.propertyNumber')}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-layer-group"></i> {t('property.create.fields.tab3.unitFloor')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-layer-group input-icon"></i>
                    <input
                      type="number"
                      className="form-input"
                      name="unitFloor"
                      value={tab3Data.unitFloor}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder={t('property.create.fields.tab3.placeholders.unitFloor')}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-calendar-alt"></i> {t('property.create.fields.tab3.propertyAge')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-calendar-alt input-icon"></i>
                    <input
                      type="number"
                      className="form-input"
                      name="realEstateAge"
                      value={tab3Data.realEstateAge}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder={t('property.create.fields.tab3.placeholders.age')}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-calculator"></i> {t('property.create.fields.tab3.unitTotalNumber')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-calculator input-icon"></i>
                    <input
                      type="number"
                      className="form-input"
                      name="unitTotalNumber"
                      value={tab3Data.unitTotalNumber}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder={t('property.create.fields.tab3.placeholders.totalUnits')}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-building"></i> {t('property.create.fields.tab3.floorsTotalNumber')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-building input-icon"></i>
                    <input
                      type="number"
                      className="form-input"
                      name="floorsTotalNumber"
                      value={tab3Data.floorsTotalNumber}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder={t('property.create.fields.tab3.placeholders.totalFloors')}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-check-square"></i> {t('property.create.fields.tab3.isPartOfCompound')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-check-square input-icon"></i>
                    <select
                      className="form-input"
                      name="isPartOfCompound"
                      value={tab3Data.isPartOfCompound ? 'true' : 'false'}
                      readOnly
                      disabled
                      onChange={() => {}}
                    >
                      <option value="true">{t('common.yes')}</option>
                      <option value="false">{t('common.no')}</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-map-marked-alt"></i> {t('property.create.fields.tab3.area')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-map-marked-alt input-icon"></i>
                    <select
                      className="form-input"
                      name="areaId"
                      value={tab3Data.areaId}
                      readOnly
                      disabled
                      onChange={() => {}}
                    >
                      <option value="">-- {t('property.create.fields.tab1.select')} --</option>
                      {(dropdowns.areas || []).map(area => (
                        <option key={area.id} value={area.id}>{area.areaNameEng || area.AreaNameEng}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-city"></i> {t('property.create.fields.tab3.city')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-city input-icon"></i>
                    <select
                      className="form-input"
                      name="cityId"
                      value={tab3Data.cityId}
                      readOnly
                      disabled
                      onChange={() => {}}
                    >
                      <option value="">-- {t('property.create.fields.tab1.select')} --</option>
                      {(dropdowns.cities || []).map(city => (
                        <option key={city.id} value={city.id}>{city.cityNameEng || city.CityNameEng}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-map-pin"></i> {t('property.create.fields.tab3.district')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-map-pin input-icon"></i>
                    <select
                      className="form-input"
                      name="districtId"
                      value={tab3Data.districtId}
                      readOnly
                      disabled
                      onChange={() => {}}
                    >
                      <option value="">-- {t('property.create.fields.tab1.select')} --</option>
                      {(dropdowns.districts || []).map(district => (
                        <option key={district.id} value={district.id}>{district.districtNameEng || district.DistrictNameEng}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-road"></i> {t('property.create.fields.tab3.streetName')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-road input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="streetName"
                      value={tab3Data.streetName}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder={t('property.create.fields.tab3.placeholders.streetName')}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-building"></i> {t('property.create.fields.tab3.buildingNumber')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-building input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="buildingNumber"
                      value={tab3Data.buildingNumber}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder={t('property.create.fields.tab3.placeholders.buildingNumber')}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-plus"></i> {t('property.create.fields.tab3.additionalNumber')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-plus input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="additionalNumber"
                      value={tab3Data.additionalNumber}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder={t('property.create.fields.tab3.placeholders.additionalNumber')}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-mail-bulk"></i> {t('property.create.fields.tab3.postalCode')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-mail-bulk input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="postalCode"
                      value={tab3Data.postalCode}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder={t('property.create.fields.tab3.placeholders.postalCode')}
                    />
                  </div>
                </div>
              </div>

              {/* Facilities and Amenities Section */}
              <div className="facilities-section">
                <label className="form-label facilities-section-label">
                  <i className="fas fa-star"></i> {t('property.create.fields.tab3.containsFacilities')}
                </label>
                <div className="facilities-section-container">
                  <div className="facilities-grid">
                  {(dropdowns.facilities || []).map(facility => {
                    const facilityId = parseInt(facility.id || facility.Id);
                    const isChecked = !isNaN(facilityId) && tab3Data.selectedFacilities[facilityId] !== undefined;
                    const quantity = isChecked ? (tab3Data.selectedFacilities[facilityId] || 1) : 1;
                    return (
                      <div
                        key={facility.id}
                          className={`facility-card ${isChecked ? 'active' : ''}`}
                          style={{ pointerEvents: 'none', opacity: isChecked ? 1 : 0.6 }}
                          onClick={() => {}}
                        >
                      
                        <div className="facility-card-content">
                          <div className="facility-icon">
                            <i className="fas fa-check-circle"></i>
                          </div>
                          <div className="facility-info">
                            <div className="facility-name">{facility.nameEng || facility.NameEng}</div>
                            {facility.nameAr || facility.NameAr ? (
                              <div className="facility-name-ar">{facility.nameAr || facility.NameAr}</div>
                            ) : null}
                          </div>
                          <div className="facility-quantity-select">
                            <select
                              className="form-select-sm facility-quantity-dropdown"
                              value={quantity}
                              disabled={!isChecked}
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => {}}
                            >
                              {Array.from({ length: 20 }, (_, i) => i + 1).map(num => (
                                <option key={num} value={num}>{num}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <input
                          type="checkbox"
                          className="facility-checkbox"
                          checked={isChecked}
                          onChange={() => {}}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    );
                  })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tab 4: Units Details */}
          {activeTab === 4 && (
            <div className="tab-panel">
              <h3 className="tab-title">{t('property.view.tabs.unitsDetails')}</h3>
              <div className="units-table-container">
                {units.length === 0 ? (
                  <div className="empty-state">{t('property.view.messages.noUnits')}</div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>{t('property.view.tableHeaders.unitNumber')}</th>
                        <th>{t('property.view.tableHeaders.unitType')}</th>
                        <th>{t('property.view.tableHeaders.unitUsage')}</th>
                        <th>{t('property.view.tableHeaders.floor')}</th>
                        <th>{t('property.view.tableHeaders.area')}</th>
                        <th>{t('property.view.tableHeaders.rooms')}</th>
                        <th>{t('property.view.tableHeaders.estDate')}</th>
                        <th>{t('property.view.tableHeaders.direction')}</th>
                        <th>{t('property.view.tableHeaders.finishing')}</th>
                        <th>{t('property.view.tableHeaders.furnished')}</th>
                        <th>{t('property.view.tableHeaders.parking')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {units.map((unit, index) => {
                        const unitType = (dropdowns.unitTypes || []).find(t => (t.id || t.Id) == unit.unitTypeId);
                        const unitUsage = (dropdowns.unitUsages || []).find(u => (u.id || u.Id) == unit.unitUsageId);
                        const directionMap = { '1': t('property.view.directions.north'), '2': t('property.view.directions.south'), '3': t('property.view.directions.east'), '4': t('property.view.directions.west'), '5': t('property.view.directions.northEast'), '6': t('property.view.directions.northWest'), '7': t('property.view.directions.southEast'), '8': t('property.view.directions.southWest') };
                        const finishingMap = { '1': t('property.view.finishing.basic'), '2': t('property.view.finishing.standard'), '3': t('property.view.finishing.premium'), '4': t('property.view.finishing.luxury'), '5': t('property.view.finishing.unfinished') };
                        
                        return (
                          <tr key={index}>
                            <td>{unit.unitNumber || t('common.dash')}</td>
                            <td>{unitType ? (unitType.nameEng || unitType.NameEng) : t('common.dash')}</td>
                            <td>{unitUsage ? (unitUsage.nameEng || unitUsage.NameEng) : t('common.dash')}</td>
                            <td>{unit.floorNumber || t('common.dash')}</td>
                            <td>{unit.unitArea || t('common.dash')}</td>
                            <td>{unit.numberOfRooms || t('common.dash')}</td>
                            <td>{unit.establishedDate || t('common.dash')}</td>
                            <td>{directionMap[unit.unitDirection] || t('common.dash')}</td>
                            <td>{finishingMap[unit.unitFinishing] || t('common.dash')}</td>
                            <td>{unit.furnished ? t('common.yes') : t('common.no')}</td>
                            <td>{unit.countOfParking || t('common.dash')}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {/* Tab 5: Attachments */}
          {activeTab === 5 && (
            <div className="tab-panel">
              <h3 className="tab-title">{t('property.view.tabs.attachments')}</h3>
              <div className="attachments-table-container">
                {attachments.length === 0 ? (
                  <div className="empty-state">{t('property.view.messages.noAttachments')}</div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>{t('property.view.tableHeaders.attachmentName')}</th>
                        <th>{t('property.view.tableHeaders.file')}</th>
                        <th>{t('property.view.tableHeaders.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attachments.map((attachment, index) => (
                        <tr key={index}>
                          <td>{attachment.name}</td>
                          <td>
                            {attachment.file ? (
                              <span className="text-info">
                                <i className="fas fa-file"></i> {attachment.file.name}
                                {attachment.file.size && ` (${(attachment.file.size / 1024).toFixed(2)} KB)`}
                              </span>
                            ) : attachment.filePath ? (
                              <span className="text-success">
                                <i className="fas fa-check-circle"></i> {t('property.view.messages.fileUploaded')}
                              </span>
                            ) : (
                              <span className="text-warning">{t('property.view.messages.noFileSelected')}</span>
                            )}
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                              {(attachment.file || attachment.filePath) && (
                                <>
                                  <button 
                                    className="btn-icon" 
                                    onClick={() => handleViewAttachment(attachment)}
                                    title={t('common.view')}
                                  >
                                    <i className="fas fa-eye" style={{ color: '#17a2b8' }}></i>
                                  </button>
                                  <button 
                                    className="btn-icon" 
                                    onClick={() => handleDownloadAttachment(attachment)}
                                    title={t('property.view.buttons.download')}
                                  >
                                    <i className="fas fa-download" style={{ color: '#28a745' }}></i>
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
      </div>

      <SuccessModal
        show={showSuccessModal}
        message="Property updated successfully."
        onClose={() => setShowSuccessModal(false)}
      />

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />

      {/* Ownership Modal - Hidden in View Mode */}
      {false && showOwnershipModal && (
        <div className="modal-overlay" onClick={() => setShowOwnershipModal(false)}>
          <div className="modal-content modal-xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                <i className="fas fa-user"></i> {editingOwnershipIndex !== null ? 'Edit' : 'Add'} Owner
              </h3>
              <button className="modal-close" onClick={() => setShowOwnershipModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-language"></i> Ownership Name Ar *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-language input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="ownershipNameAr"
                      value={ownershipFormData.ownershipNameAr}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder="Enter Arabic name"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-font"></i> Ownership Name Eng *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-font input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="ownershipNameEng"
                      value={ownershipFormData.ownershipNameEng}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder="Enter English name"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-calendar"></i> Date of Birth
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-calendar input-icon"></i>
                    <input
                      type="date"
                      className="form-input"
                      name="dob"
                      value={ownershipFormData.dob}
                      readOnly
                      disabled
                      onChange={() => {}}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-flag"></i> Nationality
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-flag input-icon"></i>
                    <select
                      className="form-input"
                      name="nationalityId"
                      value={ownershipFormData.nationalityId}
                      readOnly
                      disabled
                      onChange={() => {}}
                    >
                      <option value="">-- {t('property.create.fields.tab1.select')} --</option>
                      {(dropdowns.nationalities || []).map(nat => (
                        <option key={nat.id} value={nat.id}>{nat.nationalityNameEng || nat.NationalityNameEng}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-id-card"></i> Identity Type
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-id-card input-icon"></i>
                    <select
                      className="form-input"
                      name="identityTypeId"
                      value={ownershipFormData.identityTypeId}
                      readOnly
                      disabled
                      onChange={() => {}}
                    >
                      <option value="">-- {t('property.create.fields.tab1.select')} --</option>
                      {(dropdowns.identities || []).map(id => {
                        // Try both PascalCase and camelCase property names
                        const name = id.IdentityNameEng || id.identityNameEng || id.nameEng || id.NameEng || '';
                        return (
                          <option key={id.id} value={id.id}>{name}</option>
                        );
                      })}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-hashtag"></i> Identity Number
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-hashtag input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="identityNumber"
                      value={ownershipFormData.identityNumber}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder="Enter identity number"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-phone"></i> Mobile Country Code
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-phone input-icon"></i>
                    <select
                      className="form-input"
                      name="mobileNumberCountryCode"
                      value={ownershipFormData.mobileNumberCountryCode}
                      readOnly
                      disabled
                      onChange={() => {}}
                    >
                      <option value="">-- Select Country --</option>
                      {(dropdowns.nationalities || []).map(nat => (
                        <option key={nat.id} value={nat.phoneCode || nat.PhoneCode}>
                          {nat.phoneCode || nat.PhoneCode} - {nat.nationalityNameEng || nat.NationalityNameEng}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-mobile-alt"></i> Mobile Number
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-mobile-alt input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="mobileNumber"
                      value={ownershipFormData.mobileNumber}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder="Enter mobile number"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-envelope"></i> Email
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-envelope input-icon"></i>
                    <input
                      type="email"
                      className="form-input"
                      name="emailId"
                      value={ownershipFormData.emailId}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder="Enter email"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-university"></i> Bank
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-university input-icon"></i>
                    <select
                      className="form-input"
                      name="bankId"
                      value={ownershipFormData.bankId}
                      readOnly
                      disabled
                      onChange={() => {}}
                    >
                      <option value="">-- {t('property.create.fields.tab1.select')} --</option>
                      {(dropdowns.banks || []).map(bank => (
                        <option key={bank.id} value={bank.id}>{bank.nameEng || bank.NameEng}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-credit-card"></i> Bank Account Number
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-credit-card input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="bankAccountNumber"
                      value={ownershipFormData.bankAccountNumber}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder="Enter account number"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-map-marked-alt"></i> Area
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-map-marked-alt input-icon"></i>
                    <select
                      className="form-input"
                      name="areaId"
                      value={ownershipFormData.areaId}
                      readOnly
                      disabled
                      onChange={() => {}}
                    >
                      <option value="">-- {t('property.create.fields.tab1.select')} --</option>
                      {(dropdowns.areas || []).map(area => (
                        <option key={area.id} value={area.id}>{area.areaNameEng || area.AreaNameEng}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-city"></i> City
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-city input-icon"></i>
                    <select
                      className="form-input"
                      name="cityId"
                      value={ownershipFormData.cityId}
                      readOnly
                      disabled
                      onChange={() => {}}
                    >
                      <option value="">-- {t('property.create.fields.tab1.select')} --</option>
                      {(dropdowns.cities || []).map(city => (
                        <option key={city.id} value={city.id}>{city.cityNameEng || city.CityNameEng}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-map-pin"></i> District
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-map-pin input-icon"></i>
                    <select
                      className="form-input"
                      name="districtId"
                      value={ownershipFormData.districtId}
                      readOnly
                      disabled
                      onChange={() => {}}
                    >
                      <option value="">-- {t('property.create.fields.tab1.select')} --</option>
                      {(dropdowns.districts || []).map(district => (
                        <option key={district.id} value={district.id}>{district.districtNameEng || district.DistrictNameEng}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-road"></i> Street Name
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-road input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="streetName"
                      value={ownershipFormData.streetName}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder="Enter street name"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-building"></i> Building Number
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-building input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="buildingNumber"
                      value={ownershipFormData.buildingNumber}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder="Enter building number"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-plus-square"></i> Additional Number
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-plus-square input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="additionalNumber"
                      value={ownershipFormData.additionalNumber}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder="Enter additional number"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-mail-bulk"></i> Postal Code
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-mail-bulk input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="postalCode"
                      value={ownershipFormData.postalCode}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder="Enter postal code"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowOwnershipModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSaveOwnership}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Agent Modal - Hidden in View Mode */}
      {false && showAgentModal && (
        <div className="modal-overlay" onClick={() => setShowAgentModal(false)}>
          <div className="modal-content modal-xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                <i className="fas fa-user-tie"></i> {editingOwnershipIndex !== null ? 'Edit' : 'Add'} Agent
              </h3>
              <button className="modal-close" onClick={() => setShowAgentModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-user"></i> Select Owner *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-user input-icon"></i>
                    <select
                      className="form-input"
                      name="ownerId"
                      value={ownershipFormData.ownerId}
                      readOnly
                      disabled
                      onChange={() => {}}
                    >
                      <option value="">-- Select Owner --</option>
                      {(ownerships || []).filter(o => !o.isAgent).map((owner, idx) => (
                        <option key={idx} value={idx}>{owner.ownershipNameEng}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-id-badge"></i> Agent Number
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-id-badge input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="agentNumber"
                      value={ownershipFormData.agentNumber}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder="Enter agent number"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-language"></i> Ownership Name Ar *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-language input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="ownershipNameAr"
                      value={ownershipFormData.ownershipNameAr}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder="Enter Arabic name"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-font"></i> Ownership Name Eng *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-font input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="ownershipNameEng"
                      value={ownershipFormData.ownershipNameEng}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder="Enter English name"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-calendar"></i> Date of Birth
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-calendar input-icon"></i>
                    <input
                      type="date"
                      className="form-input"
                      name="dob"
                      value={ownershipFormData.dob}
                      readOnly
                      disabled
                      onChange={() => {}}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-flag"></i> Nationality
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-flag input-icon"></i>
                    <select
                      className="form-input"
                      name="nationalityId"
                      value={ownershipFormData.nationalityId}
                      readOnly
                      disabled
                      onChange={() => {}}
                    >
                      <option value="">-- {t('property.create.fields.tab1.select')} --</option>
                      {(dropdowns.nationalities || []).map(nat => (
                        <option key={nat.id} value={nat.id}>{nat.nationalityNameEng || nat.NationalityNameEng}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-id-card"></i> Identity Type
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-id-card input-icon"></i>
                    <select
                      className="form-input"
                      name="identityTypeId"
                      value={ownershipFormData.identityTypeId}
                      readOnly
                      disabled
                      onChange={() => {}}
                    >
                      <option value="">-- {t('property.create.fields.tab1.select')} --</option>
                      {(dropdowns.identities || []).map(id => {
                        // Try both PascalCase and camelCase property names
                        const name = id.IdentityNameEng || id.identityNameEng || id.nameEng || id.NameEng || '';
                        return (
                          <option key={id.id} value={id.id}>{name}</option>
                        );
                      })}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-hashtag"></i> Identity Number
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-hashtag input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="identityNumber"
                      value={ownershipFormData.identityNumber}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder="Enter identity number"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-phone"></i> Mobile Country Code
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-phone input-icon"></i>
                    <select
                      className="form-input"
                      name="mobileNumberCountryCode"
                      value={ownershipFormData.mobileNumberCountryCode}
                      readOnly
                      disabled
                      onChange={() => {}}
                    >
                      <option value="">-- Select Country --</option>
                      {(dropdowns.nationalities || []).map(nat => (
                        <option key={nat.id} value={nat.phoneCode || nat.PhoneCode}>
                          {nat.phoneCode || nat.PhoneCode} - {nat.nationalityNameEng || nat.NationalityNameEng}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-mobile-alt"></i> Mobile Number
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-mobile-alt input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="mobileNumber"
                      value={ownershipFormData.mobileNumber}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder="Enter mobile number"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-envelope"></i> Email
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-envelope input-icon"></i>
                    <input
                      type="email"
                      className="form-input"
                      name="emailId"
                      value={ownershipFormData.emailId}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder="Enter email"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-university"></i> Bank
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-university input-icon"></i>
                    <select
                      className="form-input"
                      name="bankId"
                      value={ownershipFormData.bankId}
                      readOnly
                      disabled
                      onChange={() => {}}
                    >
                      <option value="">-- {t('property.create.fields.tab1.select')} --</option>
                      {(dropdowns.banks || []).map(bank => (
                        <option key={bank.id} value={bank.id}>{bank.nameEng || bank.NameEng}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-credit-card"></i> Bank Account Number
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-credit-card input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="bankAccountNumber"
                      value={ownershipFormData.bankAccountNumber}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder="Enter account number"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-map-marked-alt"></i> Area
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-map-marked-alt input-icon"></i>
                    <select
                      className="form-input"
                      name="areaId"
                      value={ownershipFormData.areaId}
                      readOnly
                      disabled
                      onChange={() => {}}
                    >
                      <option value="">-- {t('property.create.fields.tab1.select')} --</option>
                      {(dropdowns.areas || []).map(area => (
                        <option key={area.id} value={area.id}>{area.areaNameEng || area.AreaNameEng}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-city"></i> City
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-city input-icon"></i>
                    <select
                      className="form-input"
                      name="cityId"
                      value={ownershipFormData.cityId}
                      readOnly
                      disabled
                      onChange={() => {}}
                    >
                      <option value="">-- {t('property.create.fields.tab1.select')} --</option>
                      {(dropdowns.cities || []).map(city => (
                        <option key={city.id} value={city.id}>{city.cityNameEng || city.CityNameEng}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-map-pin"></i> District
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-map-pin input-icon"></i>
                    <select
                      className="form-input"
                      name="districtId"
                      value={ownershipFormData.districtId}
                      readOnly
                      disabled
                      onChange={() => {}}
                    >
                      <option value="">-- {t('property.create.fields.tab1.select')} --</option>
                      {(dropdowns.districts || []).map(district => (
                        <option key={district.id} value={district.id}>{district.districtNameEng || district.DistrictNameEng}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-road"></i> Street Name
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-road input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="streetName"
                      value={ownershipFormData.streetName}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder="Enter street name"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-building"></i> Building Number
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-building input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="buildingNumber"
                      value={ownershipFormData.buildingNumber}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder="Enter building number"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-plus-square"></i> Additional Number
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-plus-square input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="additionalNumber"
                      value={ownershipFormData.additionalNumber}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder="Enter additional number"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-mail-bulk"></i> Postal Code
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-mail-bulk input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="postalCode"
                      value={ownershipFormData.postalCode}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder="Enter postal code"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAgentModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSaveOwnership}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unit Modal - Hidden in View Mode */}
      {false && showUnitModal && (
        <div className="modal-overlay" onClick={() => setShowUnitModal(false)}>
          <div className="modal-content modal-xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                <i className="fas fa-home"></i> {editingUnitIndex !== null ? 'Edit' : 'Add'} Unit
              </h3>
              <button className="modal-close" onClick={() => setShowUnitModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-hashtag"></i> Unit Number
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-hashtag input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="unitNumber"
                      value={unitFormData.unitNumber}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder="Enter unit number"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-building"></i> Unit Type
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-building input-icon"></i>
                    <select
                      className="form-input"
                      name="unitTypeId"
                      value={unitFormData.unitTypeId}
                      readOnly
                      disabled
                      onChange={() => {}}
                    >
                      <option value="">-- {t('property.create.fields.tab1.select')} --</option>
                      {(dropdowns.unitTypes || []).map(type => (
                        <option key={type.id} value={type.id}>{type.nameEng || type.NameEng}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-tag"></i> Unit Usage
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-tag input-icon"></i>
                    <select
                      className="form-input"
                      name="unitUsageId"
                      value={unitFormData.unitUsageId}
                      readOnly
                      disabled
                      onChange={() => {}}
                    >
                      <option value="">-- {t('property.create.fields.tab1.select')} --</option>
                      {(dropdowns.unitUsages || []).map(usage => (
                        <option key={usage.id} value={usage.id}>{usage.nameEng || usage.NameEng}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-layer-group"></i> Floor Number
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-layer-group input-icon"></i>
                    <input
                      type="number"
                      className="form-input"
                      name="floorNumber"
                      value={unitFormData.floorNumber}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder="Enter floor number"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-ruler-combined"></i> Unit Area
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-ruler-combined input-icon"></i>
                    <input
                      type="number"
                      step="0.01"
                      className="form-input"
                      name="unitArea"
                      value={unitFormData.unitArea}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder="Enter unit area"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-door-open"></i> Number Of Rooms
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-door-open input-icon"></i>
                    <input
                      type="number"
                      className="form-input"
                      name="numberOfRooms"
                      value={unitFormData.numberOfRooms}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder="Enter number of rooms"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-calendar"></i> Established Date
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-calendar input-icon"></i>
                    <input
                      type="date"
                      className="form-input"
                      name="establishedDate"
                      value={unitFormData.establishedDate}
                      readOnly
                      disabled
                      onChange={() => {}}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-compass"></i> Unit Direction
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-compass input-icon"></i>
                    <select
                      className="form-input"
                      name="unitDirection"
                      value={unitFormData.unitDirection}
                      readOnly
                      disabled
                      onChange={() => {}}
                    >
                      <option value="">-- {t('property.create.fields.tab1.select')} --</option>
                      <option value="1">North</option>
                      <option value="2">South</option>
                      <option value="3">East</option>
                      <option value="4">West</option>
                      <option value="5">NorthEast</option>
                      <option value="6">NorthWest</option>
                      <option value="7">SouthEast</option>
                      <option value="8">SouthWest</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-paint-brush"></i> Unit Finishing
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-paint-brush input-icon"></i>
                    <select
                      className="form-input"
                      name="unitFinishing"
                      value={unitFormData.unitFinishing}
                      readOnly
                      disabled
                      onChange={() => {}}
                    >
                      <option value="">-- {t('property.create.fields.tab1.select')} --</option>
                      <option value="1">Basic</option>
                      <option value="2">Standard</option>
                      <option value="3">Premium</option>
                      <option value="4">Luxury</option>
                      <option value="5">Unfinished</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-ruler"></i> Unit Front Length
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-ruler input-icon"></i>
                    <input
                      type="number"
                      step="0.01"
                      className="form-input"
                      name="unitFrontLength"
                      value={unitFormData.unitFrontLength}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder="Enter front length"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-ruler-horizontal"></i> Unit Length
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-ruler-horizontal input-icon"></i>
                    <input
                      type="number"
                      step="0.01"
                      className="form-input"
                      name="unitLength"
                      value={unitFormData.unitLength}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder="Enter length"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-ruler-vertical"></i> Unit Width
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-ruler-vertical input-icon"></i>
                    <input
                      type="number"
                      step="0.01"
                      className="form-input"
                      name="unitWidth"
                      value={unitFormData.unitWidth}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder="Enter width"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-arrows-alt-v"></i> Unit Height
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-arrows-alt-v input-icon"></i>
                    <input
                      type="number"
                      step="0.01"
                      className="form-input"
                      name="unitHeight"
                      value={unitFormData.unitHeight}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder="Enter height"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-check-square"></i> Include Mezzanine
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-check-square input-icon"></i>
                    <select
                      className="form-input"
                      name="includeMezzanine"
                      value={unitFormData.includeMezzanine ? 'true' : 'false'}
                      readOnly
                      disabled
                      onChange={() => {}}
                    >
                      <option value="false">No</option>
                      <option value="true">Yes</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-couch"></i> Furnished
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-couch input-icon"></i>
                    <select
                      className="form-input"
                      name="furnished"
                      value={unitFormData.furnished ? 'true' : 'false'}
                      readOnly
                      disabled
                      onChange={() => {}}
                    >
                      <option value="false">No</option>
                      <option value="true">Yes</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-utensils"></i> Kitchen Cabinets
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-utensils input-icon"></i>
                    <select
                      className="form-input"
                      name="kitchenCabinets"
                      value={unitFormData.kitchenCabinets ? 'true' : 'false'}
                      readOnly
                      disabled
                      onChange={() => {}}
                    >
                      <option value="false">No</option>
                      <option value="true">Yes</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-bolt"></i> Electricity Meter
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-bolt input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="electricityMeter"
                      value={unitFormData.electricityMeter}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder="Enter electricity meter"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-fire"></i> Gas Meter No
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-fire input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="gasMeterNo"
                      value={unitFormData.gasMeterNo}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder="Enter gas meter number"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-tint"></i> Water Meter
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-tint input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="waterMeter"
                      value={unitFormData.waterMeter}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder="Enter water meter"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-car"></i> Count Of Parking
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-car input-icon"></i>
                    <input
                      type="number"
                      className="form-input"
                      name="countOfParking"
                      value={unitFormData.countOfParking}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder="Enter parking count"
                    />
                  </div>
                </div>
              </div>

              {/* Unit Facilities and Amenities Section */}
              <div className="facilities-section" style={{ marginTop: '2rem' }}>
                <label className="form-label facilities-section-label">
                  <i className="fas fa-star"></i> {t('property.edit.modals.unit.containsFacilities')}
                </label>
                <div className="facilities-section-container">
                  {(!dropdowns.unitFacilities || dropdowns.unitFacilities.length === 0) ? (
                    <div className="empty-state" style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                      <i className="fas fa-info-circle" style={{ fontSize: '2rem', marginBottom: '1rem', color: '#bf4f1f' }}></i>
                      <p>{t('property.edit.messages.noUnitFacilitiesAvailable')}</p>
                    </div>
                  ) : (
                  <div className="facilities-grid">
                      {dropdowns.unitFacilities.map(facility => {
                      const facilityId = parseInt(facility.id || facility.Id);
                      const isChecked = !isNaN(facilityId) && unitFormData.selectedFacilities[facilityId] !== undefined;
                      const quantity = isChecked ? unitFormData.selectedFacilities[facilityId] : 1;
                      return (
                        <div
                          key={facilityId}
                          className={`facility-card unit-facility-card ${isChecked ? 'active' : ''}`}
                          style={{ pointerEvents: 'none', opacity: isChecked ? 1 : 0.6 }}
                          onClick={() => {}}
                        >
                  
                          <div className="facility-card-content">
                            <div className="facility-icon">
                              {facility.icon || facility.Icon ? (
                                <img src={facility.icon || facility.Icon} alt={facility.nameEng || facility.NameEng} />
                              ) : (
                                <i className="fas fa-check-circle"></i>
                              )}
                            </div>
                            <div className="facility-info">
                              <div className="facility-name">{facility.nameEng || facility.NameEng}</div>
                              {(facility.nameAr || facility.NameAr) && (
                                <div className="facility-name-ar">{facility.nameAr || facility.NameAr}</div>
                              )}
                            </div>
                            <div className="facility-quantity-select">
                              <select
                                className="form-select-sm facility-quantity-dropdown"
                                value={quantity}
                                disabled
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => {}}
                              >
                                {Array.from({ length: 20 }, (_, i) => i + 1).map(num => (
                                  <option key={num} value={num}>{num}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <input
                            type="checkbox"
                            className="facility-checkbox"
                            checked={isChecked}
                            onChange={() => {}}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      );
                    })}
                  </div>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowUnitModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSaveUnit}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Attachment Modal - Hidden in View Mode */}
      {false && showAttachmentModal && (
        <div className="modal-overlay" onClick={() => setShowAttachmentModal(false)}>
          <div className="modal-content modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                <i className="fas fa-paperclip"></i> {editingAttachmentIndex !== null ? 'Edit' : 'Add'} Attachment
              </h3>
              <button className="modal-close" onClick={() => setShowAttachmentModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-file-alt"></i> Attachment Name *
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-file-alt input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      name="name"
                      value={attachmentFormData.name}
                      readOnly
                      disabled
                      onChange={() => {}}
                      placeholder="Enter attachment name"
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-upload"></i> Upload File {editingAttachmentIndex === null ? '*' : ''}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-upload input-icon"></i>
                    <input
                      type="file"
                      className="form-input"
                      name="file"
                      readOnly
                      disabled
                      onChange={() => {}}
                      style={{ paddingLeft: '2.5rem' }}
                    />
                  </div>
                  {attachmentFormData.file && (
                    <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#666' }}>
                      <i className="fas fa-check-circle" style={{ color: '#28a745' }}></i> {attachmentFormData.file.name} ({(attachmentFormData.file.size / 1024).toFixed(2)} KB)
                    </div>
                  )}
                  {attachmentFormData.filePath && !attachmentFormData.file && (
                    <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#666' }}>
                      <i className="fas fa-info-circle" style={{ color: '#17a2b8' }}></i> Existing file will be kept if no new file is selected
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAttachmentModal(false)}>
                <i className="fas fa-times"></i> Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSaveAttachment}>
                <i className="fas fa-save"></i> Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyView;
